import { HttpErrorResponse } from '@angular/common/http';
import { Component, Input } from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable } from 'rxjs';
import { Constants } from 'src/app/app.constants';
import {
  StorageService,
  MerchantService,
  AuthService,
  RewardService,
  LocationService,
} from '../../../services/index';
import { Location, Reward } from '../../../models/index';

@Component({
  selector: 'reward-detail',
  templateUrl: './reward-detail.component.html',
  styleUrls: ['./reward-detail.component.css'],
})
export class RewardDetailComponent {
  @Input() smallTitle = false;
  @Input() title = '';
  @Input() subtitle: string = '';

  hasExpiry?: boolean = false;
  giftable?: boolean = true;
  form!: FormGroup;
  titleFormControl: FormControl = new FormControl();
  descriptionFormControl: FormControl = new FormControl();
  hiddenTextFormControl: FormControl = new FormControl();
  valueFormControl: FormControl = new FormControl();
  expiryFormControl: FormControl = new FormControl();
  locationFormControl: FormControl = new FormControl();

  socials: any[] = [];
  reward: Reward = {};
  locations: Location[] = [];

  locationsLoading: boolean = false;
  editMode = false;
  rewardLoading = false;
  planLimitsLoading = false;
  saving = false;
  error = false;
  submitted = false;
  disabledLocations: boolean[] = [];
  planLimits: any;
  canEditReward = false;
  titlePlaceholder = 'e.g. One free coffee';
  descriptionPlaceholder =
    'e.g. Entitles customer to any regular sized coffee of their choice';
  hiddenTextPlaceholder = 'e.g. Enter code abc123 at checkout';

  constructor(
    public constants: Constants,
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private rewardService: RewardService,
    private authService: AuthService,
    private merchantService: MerchantService,
    private locationService: LocationService,
    private storageService: StorageService
  ) {
    this.route.params.subscribe(
      (params) => (this.reward.nanoid = params['id'])
    );

    if (this.reward.nanoid) {
      this.editMode = true;
    }

    this.titleFormControl = new FormControl(
      { value: '', disabled: false },
      Validators.required
    );

    this.descriptionFormControl = new FormControl(
      { value: '', disabled: false },
      Validators.required
    );

    this.hiddenTextFormControl = new FormControl({
      value: '',
      disabled: false,
    });

    this.valueFormControl = new FormControl({ value: '', disabled: false });

    this.expiryFormControl = new FormControl(
      { value: '', disabled: false },
      []
    );

    this.locationFormControl = new FormControl(
      { value: '', disabled: false },
      Validators.required
    );

    this.form = this.fb.group({
      title: this.titleFormControl,
      description: this.descriptionFormControl,
      hiddenText: this.hiddenTextFormControl,
      value: this.valueFormControl,
      expiry: this.expiryFormControl,
      locations: this.locationFormControl,
    });
  }

  get tooltipLabel(): string {
    if (this.loading) {
      return '';
    }
    if (!this.canCreateReward) {
      if (!this.locations.length) {
        return this.constants.strings.addLocation;
      } else {
        return this.constants.strings.upgradePlanNotice;
      }
    } else {
      return '';
    }
  }

  ngOnInit() {
    this.subtitle = this.authService.getNestedUserProperty('merchant', 'name');
    this.setDynamicPlaceholders();

    if (!this.editMode) {
      this.getPlanLimits();
      this.getLocations();
    } else {
      this.getReward();
    }
  }

  get canCreateReward(): boolean {
    if (this.editMode) {
      if (this.locations?.length > 0) {
        return true;
      } else {
        return false;
      }
    } else {
      if (this.planLimits.rewards > 0 && this.locations.length > 0) {
        return true;
      } else {
        return false;
      }
    }
  }

  getPlanLimits(): void {
    this.planLimitsLoading = true;
    this.merchantService
      .getPlanLimits()
      .subscribe({
        next: (res: any) => {
          if (res) {
            this.planLimits = res;
          }
        },
        error: (res: HttpErrorResponse) => {
          this.error = true;
          this.constants.snack(res.error.message);
        },
      })
      .add(() => {
        this.planLimitsLoading = false;
      });
  }

  pageEvent(event: any) {}

  get saveEnabled(): boolean {
    return !this.form.invalid;
  }

  save(): void {
    this.submitted = true;

    if (this.form.invalid) {
      this.constants.snack('Please complete all required fields');
      return;
    }

    if (this.hasExpiry && this.expiryFormControl.value <= 0) {
      this.constants.snack('Expiry days must be greater than 0');
      return;
    }

    if (this.valueFormControl.value < 0) {
      this.constants.snack('Value cannot be less than 0');
      return;
    }

    let locations = (this.locationFormControl.value as Location[])
      .filter((el) => el._id !== undefined && el._id !== null)
      .map((el) => el._id);

    if (!locations.length) {
      this.constants.snack('Please select at least one location');
      return;
    }

    this.saving = true;

    const payload = {
      title: this.titleFormControl.value?.trim(),
      description: this.descriptionFormControl.value?.trim(),
      hiddenText: this.hiddenTextFormControl.value?.trim(),
      value: this.valueFormControl.value ?? 0,
      expiry: this.hasExpiry ? this.expiryFormControl.value : 0,
      id: this.reward._id,
      giftable: this.giftable,
      locations: locations,
    };

    if (this.editMode) {
      this.submit(this.rewardService.editReward(payload), false);
    } else {
      this.submit(this.rewardService.createReward(payload), true);
    }
  }

  submit(observable: Observable<any>, isNew: boolean): void {
    observable
      .subscribe({
        next: (res: any) => {
          if (res) {
            this.router.navigate([Constants.routes.rewards]).then(() => {
              if (isNew) {
                const existing = this.authService.getNestedUserProperty(
                  'merchant',
                  'onboard'
                );
                if (existing) {
                  this.authService.updateNestedProperty('merchant', 'onboard', {
                    location: existing.location ?? false,
                    reward: true,
                    card: existing.card ?? false,
                  });
                  this.storageService.set('checklistVisible', String(true));
                }
                window.location.reload();
              }
            });
          }
        },
        error: (res: HttpErrorResponse) => {
          this.constants.snack(res.error.message);
        },
      })
      .add(() => (this.saving = false));
  }

  getReward(): void {
    this.rewardLoading = true;
    this.rewardService
      .getReward(this.reward.nanoid!)
      .subscribe({
        next: (res: Reward) => {
          if (res) {
            this.reward = res;
            this.titleFormControl.setValue(res.title);
            this.descriptionFormControl.setValue(res.description);
            this.hiddenTextFormControl.setValue(res.hiddenText);
            this.valueFormControl.setValue(res.value);

            this.giftable = res.giftable;

            if (res.expiry ?? 0 > 0) {
              this.expiryFormControl.setValue(res.expiry);
              this.hasExpiry = true;
            } else {
              this.hasExpiry = false;
              this.expiryFormControl.setValue('');
              this.expiryFormControl.clearValidators();
            }

            this.reward.locations?.sort((a, b) =>
              a.name!.localeCompare(b.name!)
            );

            this.getLocations();
          }
        },
        error: (res: HttpErrorResponse) => {
          this.router
            .navigate(['/' + Constants.routes.dashboard], {})
            .then(() => {
              window.location.reload();
            });
        },
      })
      .add(() => (this.rewardLoading = false));
  }

  get loading(): boolean {
    return (
      this.planLimitsLoading ||
      this.rewardLoading ||
      this.saving ||
      this.locationsLoading
    );
  }

  get giftTooltip(): string {
    return `Enable to allow this reward to be gifted once, thereby transferring it to the new user. 
    
            Original recipient loses access to the reward`;
  }

  get rewardTooltip(): string {
    return `By default, rewards can be redeemed by NFC Cards. 
    
            Enable this setting to also allow for reward redemption by NFC Tags.`;
  }

  hasLocations(): void {
    if (!this.locations.length) {
      this.constants.snack('Please add a location first');
    }
  }

  getLocations(): void {
    this.locationsLoading = true;

    const subscription =
      this.authService.getUserProperty('userType') ===
      this.constants.userTypes.manager
        ? this.merchantService.getStaffLocations()
        : this.locationService.getLocations(1000, 0);

    subscription
      .subscribe({
        next: (res: any) => {
          if (res) {
            this.locations = res?.docs ?? res;
            this.locations?.sort((a, b) => a.name!.localeCompare(b.name!));

            if (this.editMode) {
              this.locationFormControl.setValue(this.reward.locations);

              for (var location of this.reward.locations ?? []) {
                if (!this.locations.some((loc) => loc._id == location._id)) {
                  this.disabledLocations.push(true);
                }
              }
            } else {
              this.locationFormControl.setValue(this.locations);
            }

            this.setCanEditReward();
          }
        },
        error: (res: HttpErrorResponse) => {
          this.error = true;
          this.constants.snack(res.error.message);
        },
      })
      .add(() => {
        this.locationsLoading = false;
      });
  }

  back() {
    this.router.navigate([Constants.routes.rewards]);
  }

  setCanEditReward() {
    this.canEditReward = (!this.editMode || this.reward._canManage) ?? false;

    if (!this.canEditReward) {
      this.titleFormControl.disable();
      this.descriptionFormControl.disable();
      this.hiddenTextFormControl.disable();
      this.valueFormControl.disable();
      this.expiryFormControl.disable();
    }
  }

  setDynamicPlaceholders() {
    const merchantType = this.authService
      .getNestedUserProperty('merchant', 'type')
      ?.toLowerCase();

    // Define placeholders based on merchant type
    const placeholders = {
      cafe: {
        title: 'e.g. One free coffee',
        description:
          'e.g. Entitles customer to any regular sized coffee of their choice',
        hiddenText: 'e.g. Show this voucher to staff at the counter',
      },
      restaurant: {
        title: 'e.g. Free starter',
        description: 'e.g. Entitles customer to any starter from our menu',
        hiddenText: 'e.g. Present this to your server when ordering',
      },
      bakery: {
        title: 'e.g. Free pastry',
        description: 'e.g. Entitles customer to any pastry of their choice',
        hiddenText: 'e.g. Valid for any pastry up to £3.50',
      },
      beauty: {
        title: 'e.g. 20% off treatment',
        description:
          'e.g. Entitles customer to 20% discount on any beauty treatment',
        hiddenText: 'e.g. Book online with code BEAUTY20',
      },
      barber: {
        title: 'e.g. Free beard trim',
        description:
          'e.g. Entitles customer to a complimentary beard trim with any haircut',
        hiddenText: 'e.g. Mention this reward when booking your appointment',
      },
      'pubs & bars': {
        title: 'e.g. Free drink',
        description:
          'e.g. Entitles customer to any house drink of their choice',
        hiddenText: 'e.g. Valid for house wine, beer, or soft drinks',
      },
      shops: {
        title: 'e.g. 10% discount',
        description: 'e.g. Entitles customer to 10% off their next purchase',
        hiddenText: 'e.g. Enter code SAVE10 at checkout',
      },
      online: {
        title: 'e.g. Free shipping',
        description:
          'e.g. Entitles customer to free shipping on their next order',
        hiddenText: 'e.g. Use code FREESHIP at checkout',
      },
    };

    // Set placeholders based on merchant type, fallback to cafe defaults
    const selectedPlaceholders =
      placeholders[merchantType as keyof typeof placeholders] ||
      placeholders.cafe;
    this.titlePlaceholder = selectedPlaceholders.title;
    this.descriptionPlaceholder = selectedPlaceholders.description;
    this.hiddenTextPlaceholder = selectedPlaceholders.hiddenText;
  }
}
